'use client';

import React, { type ReactNode } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { Icon, type IconName } from './icons/Icon';
import { Metric } from './Metric';

/**
 * Row — the app-wide list idiom: icon · title/sub · value/chevron.
 *
 * Used inside a Card (set the Card's vertical padding to 0 and stack Rows).
 * `last` drops the divider on the final row. The trailing `value` renders as
 * a Metric so figures stay column-aligned across rows.
 */
export interface RowProps {
  /** Optional leading icon, shown in a tinted chip. */
  icon?: IconName;
  /** Override the chip tint (default: soft accent wash). */
  iconTint?: string;
  title: string;
  /** Secondary line under the title. */
  sub?: string;
  /** Trailing value (rendered as a Metric). */
  value?: ReactNode;
  /** Show a trailing chevron. */
  chevron?: boolean;
  onClick?: () => void;
  /** Last row in a group → no bottom divider. */
  last?: boolean;
}

export function Row({ icon, iconTint, title, sub, value, chevron, onClick, last }: RowProps) {
  const c = useColors();
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: last ? 'none' : `0.5px solid ${c.border}`,
      }}
    >
      {icon && (
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 14,
            background: iconTint || c.accentSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={icon} size={19} color={iconTint ? c.text : c.accentInk} strokeWidth={1.9} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.text, letterSpacing: '-0.01em' }}>
          {title}
        </div>
        {sub && <div style={{ fontSize: 12.5, color: c.textDim, marginTop: 2 }}>{sub}</div>}
      </div>
      {value != null && (
        <Metric size={15} color={c.textDim}>
          {value}
        </Metric>
      )}
      {chevron && <Icon name="chevronRight" size={18} color={c.textMuted} />}
    </div>
  );
}
